import { BusinessObjectBase } from './base';
import { processServicePropertySchema } from '@schemas/business-objects/process-service-property';
import type { ProcessDataType } from './process-data-type';
import type { ProcessService } from './process-service';
import type { Id } from '../base-types';

export interface ProcessServicePropertyData {
  readonly id: Id;
  readonly name?: string;
  readonly isArray?: boolean;
  readonly isInput?: boolean;
  readonly isOutput?: boolean;
  readonly required?: boolean;
  readonly description?: string;
  readonly sequence?: number;
  readonly serviceId: Id;
  readonly service?: ProcessService;
  readonly typeId: Id;
  readonly type?: ProcessDataType;
}

export interface ProcessServiceProperty extends Readonly<ProcessServicePropertyData> {}
type ProcessServicePropertyCreateWithChildrenData =
  Partial<ProcessServicePropertyData>;

export class ProcessServiceProperty extends BusinessObjectBase {
  static readonly entityName = 'ProcessServiceProperty' as const;
  static readonly plural = 'process-service-properties';
  static schema = processServicePropertySchema;

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<ProcessServiceProperty[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new ProcessServiceProperty(data));
  }

  static async findOne(filter?: any): Promise<ProcessServiceProperty | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new ProcessServiceProperty(data) : null;
  }

  static async findById(id: Id, filter?: any): Promise<ProcessServiceProperty> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new ProcessServiceProperty(data);
  }

  static async create(
    data: Omit<ProcessServicePropertyData, 'id'>,
  ): Promise<ProcessServiceProperty> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new ProcessServiceProperty(result);
  }

  static async createMany(
    data: Omit<ProcessServicePropertyData, 'id'>[],
  ): Promise<ProcessServiceProperty[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new ProcessServiceProperty(item));
  }

  static async createWithChildren(
    data: ProcessServicePropertyCreateWithChildrenData,
  ): Promise<ProcessServiceProperty> {
    const url = `${this.baseUrl}/${this.plural}/create-with-children`;
    const result = await this.post<any>(url, data);
    return new ProcessServiceProperty(result);
  }

  static async createManyWithChildren(
    data: ProcessServicePropertyCreateWithChildrenData[],
  ): Promise<ProcessServiceProperty[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many-with-children`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new ProcessServiceProperty(item));
  }

  static async updateById(
    id: Id,
    updates: Partial<ProcessServicePropertyData>,
  ): Promise<ProcessServiceProperty> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new ProcessServiceProperty(result);
  }

  static async deleteById(id: Id): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }
}
